import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import Toast from "./Toast";
import ConfirmDialog from "./ConfirmDialog";
import SectionLoader from "./SectionLoader";
import TranslatedText from "./TranslatedText";
import { useLanguage } from "../context/LanguageContext";

const LanguageSettingsTab = ({ approvals, organization, languages: propLanguages }) => {
  const { t, currentLang } = useLanguage();
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [selectedLangCode, setSelectedLangCode] = useState("en");
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // CRUD state
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLang, setEditingLang] = useState(null);
  const [formName, setFormName] = useState("");
  const [formCode, setFormCode] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", onConfirm: null });

  const API_URL = process.env.REACT_APP_API_URL || "http://45.194.3.128:4000/api/admin/";

  const fetchLanguagesList = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}languages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setLanguages(data.data);
      }
    } catch (err) {
      console.error("Error fetching languages in settings:", err);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchLanguagesList();
  }, [fetchLanguagesList]);

  // Set initial default language from approvals
  useEffect(() => {
    if (approvals && approvals.default_language) {
      setSelectedLangCode(approvals.default_language.approval || "en");
    } else {
      setSelectedLangCode("en");
    }
  }, [approvals]);

  const handleSaveDefault = async () => {
    if (!organization) {
      setToast({ open: true, message: "Please select an organization first", severity: "warning" });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const selectedLangObj = languages.find(
        (l) => (l.short_code || "").toLowerCase() === selectedLangCode.toLowerCase()
      );
      const langName = selectedLangObj ? selectedLangObj.language : selectedLangCode;

      const response = await fetch(`${API_URL}update-approval-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          approvals: {
            default_language: {
              approval: selectedLangCode,
              users: langName,
              bv_status: "no",
            },
          },
          org_id: organization,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === "Invalid token") {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }
        setToast({ open: true, message: data.message || "Failed to save default language", severity: "error" });
      } else {
        setToast({
          open: true,
          message: `Default organization language set to ${langName} (${selectedLangCode.toUpperCase()}) successfully!`,
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Error saving default language:", err);
      setToast({ open: true, message: "Network error saving default language", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Open Add/Edit Modal
  const handleOpenDialog = (lang = null) => {
    setEditingLang(lang);
    setFormName(lang ? lang.language : "");
    setFormCode(lang ? (lang.short_code || "") : "");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLang(null);
    setFormName("");
    setFormCode("");
  };

  const handleSubmitLanguage = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    if (editingLang) {
      try {
        const response = await fetch(`${API_URL}update-language`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            language: formName.trim(),
            short_code: formCode.trim(),
            languageId: editingLang.id,
          }),
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          setToast({ open: true, message: data.message || "Update failed", severity: "error" });
        } else {
          setToast({ open: true, message: "Language updated successfully", severity: "success" });
          fetchLanguagesList();
          handleCloseDialog();
        }
      } catch (err) {
        setToast({ open: true, message: "Network error updating language", severity: "error" });
      }
    } else {
      try {
        const response = await fetch(`${API_URL}add-language`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            language: formName.trim(),
            short_code: formCode.trim(),
          }),
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          setToast({ open: true, message: data.message || "Failed to add language", severity: "error" });
        } else {
          setToast({ open: true, message: "Language added successfully", severity: "success" });
          fetchLanguagesList();
          handleCloseDialog();
        }
      } catch (err) {
        setToast({ open: true, message: "Network error adding language", severity: "error" });
      }
    }
  };

  const handleDeleteLanguage = (id) => {
    setConfirmDialog({
      open: true,
      title: "Delete Language",
      message: "Are you sure you want to delete this language? This action cannot be undone.",
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(`${API_URL}delete-language`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ languageId: id }),
          });
          const data = await response.json();

          if (!response.ok || data.success === false) {
            setToast({ open: true, message: data.message || "Delete failed", severity: "error" });
          } else {
            setToast({ open: true, message: "Language deleted successfully", severity: "success" });
            fetchLanguagesList();
          }
        } catch (err) {
          setToast({ open: true, message: "Network error deleting language", severity: "error" });
        }
      },
    });
  };

  const currentLangObj = languages.find(
    (l) => (l.short_code || "").toLowerCase() === selectedLangCode.toLowerCase()
  );

  const filteredLanguages = languages.filter((l) => {
    const term = search.toLowerCase();
    return (
      (l.language && l.language.toLowerCase().includes(term)) ||
      (l.short_code && l.short_code.toLowerCase().includes(term))
    );
  });

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
      <Toast toast={toast} setToast={setToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity="error"
      />

      {/* SECTION 1: DEFAULT LANGUAGE CONFIGURATION */}
      <Paper
        sx={{
          borderRadius: 3,
          p: { xs: 2.5, sm: 3.5 },
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          color: "#FFFFFF",
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              backgroundColor: "rgba(76, 161, 175, 0.2)",
              border: "1px solid rgba(76, 161, 175, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4CA1AF",
            }}
          >
            <TranslateIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t("org_default_language", "Organization Default Language")}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)", mt: 0.3 }}>
              {t("org_default_lang_desc", "Choose the primary language loaded automatically for mobile app users belonging to this organization.")}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: "rgba(76, 161, 175, 0.08)",
            border: "1px solid rgba(76, 161, 175, 0.2)",
          }}
        >
          <Typography variant="body2" sx={{ color: "#E0F2F1", lineHeight: 1.6 }}>
            💡 <strong>{t("how_it_works", "How it works")}:</strong> {t("how_it_works_lang_desc", "Mobile app users will automatically receive this default language upon sign-in. Users can still manually switch their preferred language anytime in their mobile app profile.")}
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "rgba(255, 255, 255, 0.7)" }}>{t("select_default_language", "Select Default Language")}</InputLabel>
              <Select
                value={selectedLangCode}
                label={t("select_default_language", "Select Default Language")}
                onChange={(e) => setSelectedLangCode(e.target.value)}
                sx={{
                  borderRadius: 2,
                  color: "#FFFFFF",
                  background: "rgba(255, 255, 255, 0.05)",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                  "&:hover fieldset": { borderColor: "#4CA1AF" },
                  "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
                }}
              >
                {languages.map((l) => (
                  <MenuItem key={l.id} value={l.short_code || l.language.toLowerCase()}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                      <span>
                        <TranslatedText text={l.language} />
                      </span>
                      <Chip
                        label={(l.short_code || "").toUpperCase()}
                        size="small"
                        sx={{
                          ml: 2,
                          height: 20,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          backgroundColor: "rgba(76, 161, 175, 0.2)",
                          color: "#4CA1AF",
                        }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={7}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.6)">
                {t("current_active_default", "Current Active Default")}:
              </Typography>
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: "16px !important", color: "#4CA1AF" }} />}
                label={
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <TranslatedText text={currentLangObj ? currentLangObj.language : selectedLangCode} />
                    <span>({selectedLangCode.toUpperCase()})</span>
                  </Box>
                }
                sx={{
                  backgroundColor: "rgba(76, 161, 175, 0.25)",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  border: "1px solid #4CA1AF",
                  px: 1,
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-start" mt={3} pt={2.5} borderTop="1px solid rgba(255, 255, 255, 0.1)">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveDefault}
            disabled={saving}
            sx={{
              background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
              color: "#ffffff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              px: 3.5,
              py: 1,
              boxShadow: "0 4px 14px rgba(76, 161, 175, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #3d8b99 0%, #115858 100%)",
                boxShadow: "0 6px 20px rgba(76, 161, 175, 0.5)",
              },
            }}
          >
            {saving ? t("saving_default_lang", "Saving Default Language...") : t("btn_save_default_lang", "Save Default Language")}
          </Button>
        </Box>
      </Paper>

      {/* SECTION 2: AVAILABLE LANGUAGES MATRIX & CRUD */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h6" fontWeight={700} color="#FFFFFF">
              {t("system_supported_languages", "System Supported Languages")}
            </Typography>
            <Chip
              label={`${filteredLanguages.length} ${t("th_languages", "Languages")}`}
              size="small"
              sx={{
                background: "rgba(76, 161, 175, 0.2)",
                color: "#4CA1AF",
                fontWeight: 600,
                border: "1px solid rgba(76, 161, 175, 0.3)",
              }}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder={t("search_language_placeholder", "Search language or code...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.6)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 240 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  color: "#FFFFFF",
                  background: "rgba(255, 255, 255, 0.05)",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
                  "&:hover fieldset": { borderColor: "#4CA1AF" },
                  "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
                },
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
                color: "#ffffff",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                py: 0.8,
                boxShadow: "0 4px 14px rgba(76, 161, 175, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #3d8b99 0%, #115858 100%)",
                  boxShadow: "0 6px 20px rgba(76, 161, 175, 0.5)",
                },
              }}
            >
              {t("btn_add_language", "Add Language")}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <SectionLoader />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: "rgba(76, 161, 175, 0.12)" }}>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700, width: 70 }}>{t("th_hash", "#")}</TableCell>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700 }}>{t("th_language", "Language")}</TableCell>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700, width: 140 }}>{t("th_short_code", "Short Code")}</TableCell>
                  <TableCell align="right" sx={{ color: "#4CA1AF", fontWeight: 700, width: 120 }}>{t("th_actions", "Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLanguages.map((lang, idx) => (
                  <TableRow
                    key={lang.id}
                    sx={{
                      "&:hover": { background: "rgba(255, 255, 255, 0.04)" },
                      borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                    }}
                  >
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 500 }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF", fontWeight: 600 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TranslateIcon sx={{ fontSize: 18, color: "#4CA1AF", opacity: 0.8 }} />
                        <TranslatedText text={lang.language} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lang.short_code ? lang.short_code.toUpperCase() : "N/A"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          backgroundColor: "rgba(76, 161, 175, 0.15)",
                          color: "#4CA1AF",
                          border: "1px solid rgba(76, 161, 175, 0.3)",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(lang)}
                        sx={{
                          color: "#4CA1AF",
                          mr: 1,
                          "&:hover": { background: "rgba(76, 161, 175, 0.15)" },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteLanguage(lang.id)}
                        sx={{
                          color: "#EF4444",
                          "&:hover": { background: "rgba(239, 68, 68, 0.15)" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredLanguages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "rgba(255, 255, 255, 0.5)", py: 4 }}>
                      {t("status_no_data", "No languages found.")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Modal */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: "#1E293B",
            color: "#FFFFFF",
            borderRadius: 3,
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid rgba(255, 255, 255, 0.1)", pb: 2 }}>
          {editingLang ? t("edit_language", "Edit Language") : t("add_new_language", "Add New Language")}
        </DialogTitle>
        <form onSubmit={handleSubmitLanguage}>
          <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              autoFocus
              label={t("field_language_name", "Language Name")}
              placeholder="e.g. English, Spanish, Telugu"
              type="text"
              fullWidth
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              InputLabelProps={{ sx: { color: "rgba(255, 255, 255, 0.7)" } }}
              InputProps={{
                sx: {
                  color: "#FFFFFF",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                  "&:hover fieldset": { borderColor: "#4CA1AF" },
                  "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
                },
              }}
            />
            <TextField
              label={t("field_short_code", "Short Code (e.g. en, te, hi)")}
              placeholder="e.g. EN, ES, TE"
              type="text"
              fullWidth
              value={formCode}
              onChange={(e) => setFormCode(e.target.value)}
              required
              InputLabelProps={{ sx: { color: "rgba(255, 255, 255, 0.7)" } }}
              InputProps={{
                sx: {
                  color: "#FFFFFF",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                  "&:hover fieldset": { borderColor: "#4CA1AF" },
                  "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}>
            <Button onClick={handleCloseDialog} sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
              {t("btn_cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
                fontWeight: 600,
                px: 3,
              }}
            >
              {editingLang ? t("btn_save", "Save Changes") : t("btn_add", "Create")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default LanguageSettingsTab;
